import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  closeforgotPasswordModal,
  openLoginModal,
} from "../../features/slices/ModalSlice";
import { updateSelectedItem } from "../../features/slices/CommonSlice";
import { useMediaQuery } from "react-responsive";
import PhoneNumberField from "./PhoneNumberField";
import { parsePhoneInput, isPhoneNumberAcceptable } from "../../utils/phoneValidation";
import ChatBotOption from "./ChatBotOption";
import { forgotPassword, resetPassword } from "../../api/services/password";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;
const MIN_PASSWORD_LENGTH = 8;

export const FORGOT_PASSWORD_STEPS = {
  phone: "phone",
  otp: "otp",
  password: "password",
  done: "done",
};

/** Pulls a readable message and error code out of an axios error. */
function parseApiError(err, fallback) {
  const data = err?.response?.data;
  const code = data?.code || data?.error?.code || data?.error_code;
  const message =
    (typeof data === "string" && data) ||
    data?.message ||
    data?.error?.message ||
    (typeof data?.error === "string" && data.error) ||
    data?.detail ||
    fallback;
  return { status: err?.response?.status, code, message };
}

export default function ForgotPasswordActions({ onStepChange }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [step, setStep] = useState(FORGOT_PASSWORD_STEPS.phone);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneE164, setPhoneE164] = useState("");
  const [isvalid, setIsValid] = useState(false);
  const [phoneHint, setPhoneHint] = useState("");
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const [countdown, setCountdown] = useState(RESEND_SECONDS);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (onStepChange) onStepChange(step);
  }, [step, onStepChange]);

  useEffect(() => {
    if (step !== FORGOT_PASSWORD_STEPS.otp || countdown <= 0) return;
    const timeoutId = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timeoutId);
  }, [step, countdown]);

  const handlePhoneNumberChange = (value) => {
    setError("");
    setPhoneInput(value || "");
    const parsed = parsePhoneInput(value);
    setPhoneE164(parsed.isValid ? parsed.e164 : "");
    setIsValid(parsed.isValid);
  };

  const sendOtp = async () => {
    const res = await forgotPassword({ phone: phoneE164 });
    setPhoneHint(res?.data?.phone_hint || "");
    setInfoMessage(
      res?.message ||
        "If an account exists for this number, an OTP has been sent to it."
    );
  };

  const submitPhone = async () => {
    if (!isvalid || !isPhoneNumberAcceptable(phoneE164)) {
      setError("Phone number is not Valid");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await sendOtp();
      setOtp(new Array(OTP_LENGTH).fill(""));
      setCountdown(RESEND_SECONDS);
      setStep(FORGOT_PASSWORD_STEPS.otp);
    } catch (err) {
      setError(parseApiError(err, "Failed to send OTP").message);
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    setError("");
    setOtp(new Array(OTP_LENGTH).fill(""));
    setCountdown(RESEND_SECONDS);
    try {
      await sendOtp();
    } catch (err) {
      setError(parseApiError(err, "Failed to resend OTP").message);
    }
  };

  const handleOtpChange = (element, index) => {
    setError("");
    let digits = element.value.replace(/[^0-9]/g, "");
    // Typing into a filled box appends to the old digit; keep only the new one.
    if (digits.length > 1 && otp[index] && digits.startsWith(otp[index]))
      digits = digits.slice(1);
    const next = [...otp];
    if (!digits) {
      next[index] = "";
      setOtp(next);
      return;
    }
    // Fast typing or SMS autofill can land several digits in one box.
    digits
      .slice(0, OTP_LENGTH - index)
      .split("")
      .forEach((d, i) => (next[index + i] = d));
    setOtp(next);
    otpRefs.current[Math.min(index + digits.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otp[index] && index > 0)
      otpRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (event) => {
    const digits = event.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, OTP_LENGTH);
    if (!digits) return;
    event.preventDefault();
    const next = new Array(OTP_LENGTH).fill("");
    digits.split("").forEach((d, i) => (next[i] = d));
    setOtp(next);
    otpRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus();
  };

  // The API checks the OTP together with the new password, so this step only
  // collects it; a wrong code comes back from the reset call.
  const validateOtp = () => {
    if (otp.join("").length !== OTP_LENGTH) {
      setError(`Please enter the ${OTP_LENGTH} digit OTP`);
      return;
    }
    setError("");
    setStep(FORGOT_PASSWORD_STEPS.password);
  };

  const updatePassword = async () => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must have atleast ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Both passwords should match");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await resetPassword({
        phone: phoneE164,
        otp: otp.join(""),
        new_password: password,
        confirm_password: confirmPassword,
      });
      setStep(FORGOT_PASSWORD_STEPS.done);
    } catch (err) {
      const { status, code, message } = parseApiError(
        err,
        "Failed to reset password"
      );
      if (status === 422 || code === "PASSWORD_RESET_UNAVAILABLE") {
        setError(
          "This account does not use a password to log in. Please contact support."
        );
      } else if (/otp|code/i.test(message)) {
        setOtp(new Array(OTP_LENGTH).fill(""));
        setStep(FORGOT_PASSWORD_STEPS.otp);
        setError(message);
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openLogin = () => {
    if (isMobile) {
      dispatch(updateSelectedItem("login"));
    } else {
      dispatch(closeforgotPasswordModal());
      dispatch(openLoginModal());
    }
  };

  const errorBox = (
    <div className="incorrect-details-div mb-3">
      {error && <p className="text-center message">{error}</p>}
      <ChatBotOption />
    </div>
  );

  if (step === FORGOT_PASSWORD_STEPS.otp) {
    return (
      <div className="forgot-password-otp text-center">
        {isMobile && (
          <p className="text-center" style={{ color: "#EFC04B" }}>
            Mobile Verification
          </p>
        )}
        <p className="text--normal mb-2">
          <strong>Please enter the One Time Password to verify your account</strong>
        </p>
        <p className="text--small low-opacity mb-1">
          {phoneHint
            ? `A ${OTP_LENGTH} digit One Time Password has been sent to ${phoneHint}`
            : infoMessage}
        </p>
        {error && (
          <p className="text--small mb-1" style={{ color: "rgb(255, 64, 64)" }}>
            {error}
          </p>
        )}
        <div className="otp-inputs d-flex m-auto justify-content-center">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (otpRefs.current[index] = el)}
              className="otp-field m-2 text-center"
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              value={digit}
              onChange={(e) => handleOtpChange(e.target, index)}
              onKeyDown={(e) => handleOtpKeyDown(e, index)}
              onPaste={handleOtpPaste}
              onFocus={(e) => e.target.select()}
            />
          ))}
        </div>
        <button
          className="login-btn mb-3 mt-3 p-2 text-center w-75"
          onClick={validateOtp}
        >
          Validate
        </button>
        <p className="text--medium">
          Didn't receive OTP?{" "}
          {countdown > 0 ? (
            `Resend in ${countdown} s`
          ) : (
            <span
              onClick={resendOtp}
              className="blue--text text-decoration-underline"
              style={{ cursor: "pointer" }}
            >
              Resend
            </span>
          )}
        </p>
        <p
          className="text--small low-opacity text-decoration-underline"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setError("");
            setStep(FORGOT_PASSWORD_STEPS.phone);
          }}
        >
          Change mobile number
        </p>
      </div>
    );
  }

  if (step === FORGOT_PASSWORD_STEPS.done) {
    return (
      <div>
        <p className="text--normal mb-2">
          <strong>Password Reset</strong>
        </p>
        <p className="text--small low-opacity">
          Your password has been reset successfully. Click below to login
        </p>
        <button className="login-btn mb-3 mt-3 p-2" onClick={openLogin}>
          Login
        </button>
      </div>
    );
  }

  if (step === FORGOT_PASSWORD_STEPS.password) {
    return (
      <div>
        {errorBox}
        <p className="text--normal mb-2">
          <strong>Set New Password</strong>
        </p>
        <p className="text--small low-opacity">
          Your new password must be different from previously used password
        </p>
        <b>
          Password
          <span aria-hidden="true" className="required-asterisk">
            {" *"}
          </span>
        </b>
        <div className="password overflow-hidden">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            id="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => {
              setError("");
              setPassword(e.target.value);
            }}
          />
          <i
            className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`}
            onClick={() => setShowPassword(!showPassword)}
            style={{ fontSize: "18px", marginTop: "-1px" }}
          ></i>
        </div>
        {password.length < MIN_PASSWORD_LENGTH && (
          <p className="normal--message">
            Password must be atleast {MIN_PASSWORD_LENGTH} characters
          </p>
        )}
        <b>
          Confirm Password
          <span aria-hidden="true" className="required-asterisk">
            {" *"}
          </span>
        </b>
        <div className="password overflow-hidden">
          <input
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            id="confirmPassword"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => {
              setError("");
              setConfirmPassword(e.target.value);
            }}
          />
          <i
            className={`bi ${showConfirmPassword ? "bi-eye" : "bi-eye-slash"}`}
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            style={{ fontSize: "18px", marginTop: "-1px" }}
          ></i>
        </div>
        {(!confirmPassword || password !== confirmPassword) && (
          <p className="normal--message">Password should match</p>
        )}
        <button
          className="login-btn mb-3 mt-3 p-2"
          onClick={updatePassword}
          disabled={submitting}
        >
          {submitting ? "Please wait..." : "Submit"}
        </button>
      </div>
    );
  }

  return (
    <div>
      {errorBox}
      <p className="text--normal mb-2">
        <strong>Forgot Password?</strong>
      </p>
      <p className="text--small low-opacity">
        We will send an OTP on your registered Mobile Number. Please enter your
        Mobile Number.
      </p>
      <b>
        Mobile
        <span aria-hidden="true" className="required-asterisk">
          {" *"}
        </span>
      </b>
      <div className="input-group">
        <PhoneNumberField
          placeholder="Enter phone number"
          value={phoneInput}
          className={`text-input mb-3   ${
            isvalid ? "form-control is-valid" : ""
          }`}
          onChange={handlePhoneNumberChange}
          defaultCountry="IN"
        />
      </div>
      <button
        className="login-btn mb-3 mt-3 p-2"
        onClick={submitPhone}
        disabled={submitting}
      >
        {submitting ? "Please wait..." : "Submit"}
      </button>
    </div>
  );
}
